import React, { useEffect, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  FormActions,
  RequiredNote,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../Components/Form';
import {
  PROC_TYPE_OPTIONS,
  PROCUREMENT_TYPE_HEADER,
} from '../utils/awardingHelpers';

const INITIAL_FORM = {
  procurementNumber: '',
  // Awarding is Product Hydro only (ProcurementTypeHeader = 1).
  procurementTypeHeader: PROCUREMENT_TYPE_HEADER.productHydro,
  titleProcurement: '',
  procurementType: '',
  description: '',
};

const VALIDATION = {
  procurementNumber: {
    label: 'Procurement number',
    rules: [required()],
  },
  titleProcurement: { label: 'Title', rules: [required()] },
  procurementType: { label: 'Proc type', rules: [required()] },
};
const FIELD_ORDER = [
  'procurementNumber',
  'titleProcurement',
  'procurementType',
];

const CreateAwardingModal = ({
  isOpen,
  toggle,
  onNext,
  isSubmitting = false,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors = validateForm(form, VALIDATION);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) focusFirstError(nextErrors, FIELD_ORDER);
    return !hasErrors(nextErrors);
  };

  const handleNext = () => {
    if (!validate()) return;
    return onNext({
      ...form,
      procurementNumber: form.procurementNumber.trim(),
      titleProcurement: form.titleProcurement.trim(),
      description: form.description.trim(),
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size='lg'>
      <ModalBody className='p-3 p-sm-4'>
        <div className='d-flex justify-content-between align-items-start mb-1'>
          <h5 className='fw-bold mb-0'>Create New Awarding</h5>
          <button
            type='button'
            className='btn-close'
            onClick={toggle}
            aria-label='Close'
          ></button>
        </div>
        <p className='text-muted fs-13 mb-4'>
          Start a new awarding by filling in the details below.
        </p>

        <RequiredNote />

        <FormGrid columns={2}>
          <TextField
            span='full'
            label='Procurement number'
            name='procurementNumber'
            placeholder='e.g. PRC-2026-001'
            required
            value={form.procurementNumber}
            error={errors.procurementNumber}
            onChange={e => handleChange('procurementNumber', e.target.value)}
          />

          <TextField
            label='Title'
            name='titleProcurement'
            placeholder='Enter procurement title'
            required
            value={form.titleProcurement}
            error={errors.titleProcurement}
            onChange={e => handleChange('titleProcurement', e.target.value)}
          />
          <SelectField
            label='Proc type'
            name='procurementType'
            required
            options={PROC_TYPE_OPTIONS}
            value={form.procurementType}
            error={errors.procurementType}
            onChange={value => handleChange('procurementType', value ?? '')}
          />
          <TextAreaField
            span='full'
            label='Description'
            name='description'
            rows={4}
            maxLength={500}
            placeholder='What is this awarding for?'
            value={form.description}
            onChange={e => handleChange('description', e.target.value)}
          />
        </FormGrid>

        <FormActions>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={toggle}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            className='px-4'
            onClick={handleNext}
            loading={isSubmitting}
          >
            {isSubmitting ? (
              'Saving...'
            ) : (
              <>
                Next<i className='ri-arrow-right-line align-middle ms-2'></i>
              </>
            )}
          </ActionButton>
        </FormActions>
      </ModalBody>
    </Modal>
  );
};

export default CreateAwardingModal;
